import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, zip } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AccountGroup } from './account-group';
import { AuthedAccountGroupClientService } from './authed-account-group-client.service';

const GET_MULTI_BATCH_SIZE = 50;

@Injectable({ providedIn: 'root' })
export class AccountGroupService {
  currentAccountGroup$$ = new BehaviorSubject<AccountGroup>(null);

  public readonly currentAccountGroup$ = this.currentAccountGroup$$.pipe(
    filter((accountGroup) => accountGroup !== null),
    distinctUntilChanged((p: AccountGroup, q: AccountGroup) => p.accountGroupId === q.accountGroupId),
  );

  public readonly currentAccountGroup = toSignal<AccountGroup>(this.currentAccountGroup$);

  public readonly currentAccountGroupId = toSignal<string>(
    this.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.accountGroupId)),
  );

  constructor(private _accountGroupClient: AuthedAccountGroupClientService) {}

  switchAccountGroup(accountGroup: AccountGroup): void {
    this.currentAccountGroup$$.next(accountGroup);
  }

  getAccountGroup(accountGroupId: string): Observable<AccountGroup> {
    return this._accountGroupClient
      .getAccountGroups([accountGroupId])
      .pipe(map((accountGroups) => accountGroups[0] || null));
  }

  getAccountGroups(accountGroupIds: string[]): Observable<AccountGroup[]> {
    let batchIds: string[] = [];
    const requests$: Observable<AccountGroup[]>[] = [];
    accountGroupIds.forEach((accountGroupId) => {
      batchIds.push(accountGroupId);
      if (batchIds.length === GET_MULTI_BATCH_SIZE) {
        // TODO: Retrys
        requests$.push(this._accountGroupClient.getAccountGroups(batchIds));
        batchIds = [];
      }
    });
    if (batchIds.length > 0) {
      requests$.push(this._accountGroupClient.getAccountGroups(batchIds));
      batchIds = [];
    }

    // Return the joined stream once all batches are complete
    return zip(...requests$).pipe(
      map((responses) => {
        return responses.reduce((out, response) => out.concat(response), []);
      }),
    );
  }

  get currentAccountGroupId$(): Observable<string> {
    return this.currentAccountGroup$.pipe(map((accountGroup) => accountGroup.accountGroupId));
  }

  /**
   * Refreshes the current account group data from the server
   * @returns Observable that completes when the account group has been refreshed
   */
  refreshCurrentAccountGroup(): Observable<AccountGroup> {
    return this.currentAccountGroupId$.pipe(
      take(1),
      switchMap((accountGroupId) => this.getAccountGroup(accountGroupId)),
      tap((accountGroup) => {
        if (accountGroup) {
          this.currentAccountGroup$$.next(accountGroup);
        }
      }),
    );
  }
}
